import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

import RegionEndpoints from '../endpoints/RegionEndpoints'
import ClientEndpoints from '../endpoints/ClientEndpoints'
import IdentityEndpoints from '../endpoints/IdentityEndpoints'
import EventEndpoints from '../endpoints/EventEndpoints'

import { WorkRegion } from '../model/WorkRegion'
import { SimpleClient } from '../model/Client'
import { SimpleIdentity } from '../model/Identity'
import { SimpleEvent } from '../model/Event'
import { EventRequest } from '../requests/EventRequest'

interface ApplicationState {
  // Loading state
  waitingFor: number
  
  // Data
  regions: WorkRegion[]
  clients: SimpleClient[]
  identities: SimpleIdentity[]
  pendingIdentities: SimpleIdentity[]
  events: SimpleEvent[]
  
  // Loading actions
  startLoading: () => void
  doneLoading: () => void
  
  // Data actions
  refreshRegions: () => Promise<WorkRegion[]>
  refreshClients: () => Promise<SimpleClient[]>
  refreshIdentities: (clientId?: number, eventId?: number) => Promise<SimpleIdentity[]>
  refreshPendingIdentities: () => Promise<SimpleIdentity[]>
  refreshEvents: () => Promise<SimpleEvent[]>
  deleteRegion: (region: WorkRegion) => Promise<void>
  createEvent: (eventData: EventRequest) => Promise<SimpleEvent>
  discardEvent: (event: SimpleEvent) => Promise<void>
  
  // Utility actions
  clearAll: () => void
  
  // Computed getters
  isLoading: () => boolean
  getRegionById: (id: number) => WorkRegion | undefined
  getClientById: (id: number) => SimpleClient | undefined
  getEventById: (id: number) => SimpleEvent | undefined
  getActiveClients: () => SimpleClient[]
}

export const useApplicationStore = create<ApplicationState>()(
  devtools(
    (set, get) => ({
      // Initial state
      waitingFor: 0,
      regions: [],
      clients: [],
      identities: [],
      pendingIdentities: [],
      events: [],
      
      // Loading actions
      startLoading: () => set((state) => ({ waitingFor: state.waitingFor + 1 })),
      doneLoading: () => set((state) => ({ 
        waitingFor: Math.max(0, state.waitingFor - 1) 
      })),
      
      // Data actions
      refreshRegions: async () => {
        const { startLoading, doneLoading } = get()
        startLoading()
        try {
          const regions = await RegionEndpoints.getRegionList()
          regions.sort((a, b) => a.name.localeCompare(b.name))
          set({ regions })
          doneLoading()
          return regions
        } catch (error) {
          console.error(error)
          doneLoading()
          throw error
        }
      },
      
      refreshClients: async () => {
        const { startLoading, doneLoading } = get()
        startLoading()
        try {
          const clients = await ClientEndpoints.getClientList()
          clients.sort((a, b) => a.name.localeCompare(b.name))
          set({ clients })
          doneLoading()
          return clients
        } catch (error) {
          console.error(error)
          doneLoading()
          throw error
        }
      },
      
      refreshIdentities: async (clientId?: number, eventId?: number) => {
        const { startLoading, doneLoading } = get()
        startLoading()
        try {
          const identities = await IdentityEndpoints.getIdentityList(
            clientId, 
            undefined, // firstName
            undefined, // lastName
            undefined, // limit
            undefined, // startAt
            undefined, // search
            false      // includeArchived
          )
          identities.sort(
            (a, b) =>
              a.firstName.localeCompare(b.firstName) ||
              a.lastName.localeCompare(b.lastName)
          )
          set({ identities })
          doneLoading()
          return identities
        } catch (error) {
          console.error(error)
          doneLoading()
          throw error
        }
      },
      
      refreshPendingIdentities: async () => {
        const { startLoading, doneLoading } = get()
        startLoading()
        try {
          const pendingIdentities = await IdentityEndpoints.getPendingIdentityList()
          pendingIdentities.sort(
            (a, b) =>
              a.firstName.localeCompare(b.firstName) ||
              a.lastName.localeCompare(b.lastName)
          )
          set({ pendingIdentities })
          doneLoading()
          return pendingIdentities
        } catch (error) {
          console.error(error)
          doneLoading()
          throw error
        }
      },
      
      refreshEvents: async () => {
        const { startLoading, doneLoading } = get()
        startLoading()
        try {
          const events = await EventEndpoints.getEventList()
          events.sort((a, b) => a.name.localeCompare(b.name))
          set({ events })
          doneLoading()
          return events
        } catch (error) {
          console.error(error)
          doneLoading()
          throw error
        }
      },
      
      deleteRegion: async (region: WorkRegion) => {
        const { startLoading, doneLoading } = get()
        startLoading()
        try {
          await RegionEndpoints.deleteRegion(region.id)
          set((state) => ({
            regions: state.regions.filter(r => r.id !== region.id)
          }))
          doneLoading()
        } catch (error) {
          console.error(error)
          doneLoading()
          throw error
        }
      },
      
      createEvent: async (eventData: EventRequest) => {
        const { startLoading, doneLoading } = get()
        startLoading()
        try {
          const newEvent = await EventEndpoints.createEvent(eventData)
          set((state) => ({
            events: [...state.events, newEvent]
          }))
          doneLoading()
          return newEvent
        } catch (error) {
          console.error(error)
          doneLoading()
          throw error
        }
      },
      
      discardEvent: async (event: SimpleEvent) => {
        const { startLoading, doneLoading } = get()
        startLoading()
        try {
          await EventEndpoints.discardEvent(event.id)
          set((state) => ({
            events: state.events.filter(e => e.id !== event.id)
          }))
          doneLoading()
        } catch (error) {
          console.error(error)
          doneLoading()
          throw error
        }
      },
      
      clearAll: () => set({
        regions: [],
        clients: [],
        identities: [],
        pendingIdentities: [],
        events: [],
        waitingFor: 0
      }),
      
      // Computed getters
      isLoading: () => get().waitingFor > 0,
      getRegionById: (id: number) => get().regions.find(r => r.id === id),
      getClientById: (id: number) => get().clients.find(c => c.id === id),
      getEventById: (id: number) => get().events.find(e => e.id === id),
      getActiveClients: () => get().clients.filter(c => !c.dateArchived),
    }),
    {
      name: 'application-store',
    }
  )
)